/**
 * Shared Analytics Components
 * Reusable UI components for analytics tabs
 */

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Download, FileText, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ROW_COUNT_OPTIONS, DEFAULT_ROW_COUNT } from "../../lib/chartUtils";

// Stat Card for summary section
export const StatCard = ({ title, value, subtitle, icon: Icon, color = "primary" }) => (
  <Card className="stat-card" data-testid={`stat-${title.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="stat-label">{title}</p>
        <p className={`stat-value mt-1 ${color === "success" ? "text-green-600" : color === "danger" ? "text-red-600" : ""}`}>
          {value}
        </p>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
    </div>
  </Card>
);

// Chart Card wrapper with optional drill-down
export const ChartCard = ({ title, children, className = "", onDrillDown }) => (
  <Card className={className}>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-lg">{title}</CardTitle>
      {onDrillDown && (
        <Button variant="ghost" size="sm" onClick={onDrillDown}>
          Details <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </CardHeader>
    <CardContent>
      <div className="h-80">
        {children}
      </div>
    </CardContent>
  </Card>
);

// Table Card with export options and row count selector
export const TableCard = ({ 
  title, 
  onExportCSV, 
  onExportPDF, 
  children,
  rowCount,
  onRowCountChange,
  showRowSelector = true,
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
      <CardTitle className="text-lg">{title}</CardTitle>
      <div className="flex items-center gap-2">
        {showRowSelector && onRowCountChange && (
          <Select value={String(rowCount || DEFAULT_ROW_COUNT)} onValueChange={(v) => onRowCountChange(Number(v))}>
            <SelectTrigger className="w-24 h-8 text-xs" data-testid="row-count-select">
              <SelectValue placeholder="Rows" />
            </SelectTrigger>
            <SelectContent>
              {ROW_COUNT_OPTIONS.map(n => (
                <SelectItem key={n} value={String(n)}>Top {n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {onExportCSV && (
          <Button variant="outline" size="sm" onClick={onExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        )}
        {onExportPDF && (
          <Button variant="outline" size="sm" onClick={onExportPDF} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        {children}
      </div>
    </CardContent>
  </Card>
);

// Custom Tooltip for charts
export const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      <p className="font-medium text-sm mb-2">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {formatter ? formatter(entry.value) : entry.value}
        </p>
      ))}
    </div>
  );
};

// Status Badge
const STATUS_LABELS = {
  bidding: "Bidding",
  bid_complete: "Bid Complete",
  precon: "Precon",
  active: "Active",
  complete: "Complete",
  archive: "Archive",
  not_bid: "Not Bid",
  not_won: "Not Won",
};
export const StatusBadge = ({ status }) => (
  <span className={`badge-status badge-${status}`}>{STATUS_LABELS[status] || status}</span>
);

// Data Table component
export const DataTable = ({ headers, children }) => (
  <Table>
    <TableHeader>
      <TableRow className="table-header">
        {headers.map((header, i) => (
          <TableHead key={i} className={header.align === "right" ? "text-right" : ""}>
            {header.label}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
    <TableBody>
      {children}
    </TableBody>
  </Table>
);

// Clickable Table Row that links to job detail
export const ClickableTableRow = ({ jobId, children, className = "" }) => (
  <TableRow className={`${className} cursor-pointer hover:bg-muted/50`}>
    {children}
  </TableRow>
);

// Job Link Cell - wraps content in a link to job detail
export const JobLinkCell = ({ jobId, children }) => (
  <TableCell>
    <Link 
      to={`/jobs/${jobId}`} 
      className="text-primary hover:underline font-medium"
      data-testid={`job-link-${jobId}`}
    >
      {children}
    </Link>
  </TableCell>
);

// Empty State component with contextual messaging
export const EmptyState = ({ 
  icon: Icon, 
  title, 
  description, 
  actionLabel, 
  actionHref 
}) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    {Icon && <Icon className="w-12 h-12 text-muted-foreground mb-4" />}
    <h3 className="text-lg font-medium text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground max-w-md mb-4">{description}</p>
    {actionLabel && actionHref && (
      <Link to={actionHref}>
        <Button variant="outline">{actionLabel}</Button>
      </Link>
    )}
  </div>
);

// Contextual empty states for each tab type
export const EMPTY_STATES = {
  profitability: {
    title: "No profitability data available",
    description: "Start tracking estimates and actual costs on your jobs to see profitability metrics. Add labor estimates, material estimates, and log actual costs.",
    actionLabel: "Go to Jobs",
    actionHref: "/jobs",
  },
  labor: {
    title: "No labor data available",
    description: "Add labor costs to your jobs to track productivity. Go to a job detail page and log labor hours for employees.",
    actionLabel: "View Jobs",
    actionHref: "/jobs",
  },
  materials: {
    title: "No material data available", 
    description: "Track material usage by adding material costs to your jobs. This helps monitor spending patterns and identify top material expenses.",
    actionLabel: "View Jobs",
    actionHref: "/jobs",
  },
  revenue: {
    title: "No revenue data available",
    description: "Add billings to your jobs to track revenue and cash flow. Monitor sent vs received billings and accounts receivable aging.",
    actionLabel: "View Jobs",
    actionHref: "/jobs",
  },
  employees: {
    title: "No employee data available",
    description: "Employee performance metrics appear when you log labor costs. Track hours, earnings, and productivity for each team member.",
    actionLabel: "Manage Employees",
    actionHref: "/employees",
  },
  seasonal: {
    title: "No seasonal trend data available",
    description: "Seasonal trends show job volume and revenue patterns over time. Add jobs with start dates to see monthly and quarterly trends.",
    actionLabel: "Create a Job",
    actionHref: "/jobs/new",
  },
};

// Reexport table components for convenience
export { Table, TableBody, TableCell, TableHead, TableHeader, TableRow };
